import { Canvas, type CanvasProps, CRTPages, CRTScreen } from "@mike/crt";
import { useGLTF, useTexture } from "@react-three/drei";
import { Suspense, useCallback, useRef, useState } from "react";
import PressStart2PFont from "../../assets/fonts/PressStart2P.json";
import crtTv from "../../assets/models/crt_tv/scene.gltf";
import arcadeCarpetRed from "../../assets/textures/arcade-carpet-red.png";
import spaceHdri from "../../assets/textures/space-hdri.png";
import { ControlOverlay } from "../../components/ControlOverlay";
import { Routes } from "../../Routes";
import { CameraControls } from "./camera/CameraControls";
import { CameraFlyIn } from "./camera/CameraFlyIn";
import { PartyLights } from "./components/PartyLight";
import { RoomFloor } from "./components/RoomFloor";
import { SceneBackground } from "./components/SceneBackground";
import { SceneFooter } from "./components/SceneFooter";
import { SpaceSky } from "./components/SpaceSky";
import { TestColorScreen } from "./components/TestColorScreen";
import { GLTFPreloader, LoadedNotifier, TexturePreloader } from "./helpers";

useGLTF.preload(crtTv);
useTexture.preload(spaceHdri);
useTexture.preload(arcadeCarpetRed);

export interface MainSceneProps {
  /** Called when all assets have finished loading */
  onAssetsLoaded: () => void;
  /** Set to true to start the camera fly-in animation */
  startAnimation?: boolean;
  /** Called when camera has moved from home position */
  onCameraMoved?: (moved: boolean) => void;
  /** Whether the camera has moved (for showing return button) */
  cameraMoved?: boolean;
  /** Set to true to trigger return to home animation */
  returnToHome?: boolean;
  /** Called when return animation completes */
  onReturnComplete?: () => void;
  /** Called when return button is pressed (mobile overlay) */
  onReturn?: () => void;
}

// Camera fly-in starting position (far away, looking down slightly)
const CAMERA_START_POSITION: [number, number, number] = [0, 1, 18];

const CanvasOptions: CanvasProps = {
  shadows: true,
  camera: {
    fov: 50,
    near: 0.1,
    far: 100,
    // Start at fly-in position to avoid flash of zoomed-in view
    position: CAMERA_START_POSITION,
  },
  gl: {
    antialias: true,
    alpha: true,
    powerPreference: "high-performance",
  },
  style: {
    position: "absolute",
    inset: 0,
    backgroundColor: "transparent",
  },
};

export function MainScene({
  onAssetsLoaded,
  startAnimation = false,
  onCameraMoved,
  cameraMoved = false,
  returnToHome,
  onReturnComplete,
  onReturn,
}: MainSceneProps) {
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);
  const [tvPoweredOn, setTvPoweredOn] = useState(false);

  // Ref for CRT control methods (volume, channel)
  const crtRef = useRef<CRTScreen.RootHandle>(null);

  const { scene: tvScreenScene } = useGLTF(crtTv as string);
  if (!tvScreenScene) {
    throw new Error("Failed to load CRT TV scene");
  }

  const handleAssetsLoaded = useCallback(() => {
    setAssetsLoaded(true);
    onAssetsLoaded();
  }, [onAssetsLoaded]);

  // Turn on TV after intro animation completes
  const handleIntroComplete = useCallback(() => {
    setIntroComplete(true);
    setTvPoweredOn(true);
  }, []);

  // Toggle TV power when button is pressed
  const handlePowerToggle = useCallback(() => {
    setTvPoweredOn((prev) => !prev);
  }, []);

  // Mobile overlay control handlers
  const handleVolumeUp = useCallback(() => {
    crtRef.current?.volumeUp();
  }, []);

  const handleVolumeDown = useCallback(() => {
    crtRef.current?.volumeDown();
  }, []);

  const handleChannelUp = useCallback(() => {
    crtRef.current?.channelUp();
  }, []);

  const handleChannelDown = useCallback(() => {
    crtRef.current?.channelDown();
  }, []);

  return (
    <>
      <Canvas {...CanvasOptions}>
        {/* Preload assets */}
        <Suspense fallback={null}>
          <TexturePreloader src={spaceHdri} />
          <TexturePreloader src={arcadeCarpetRed} />
          <GLTFPreloader src={crtTv} />
          <LoadedNotifier onLoaded={handleAssetsLoaded} />
        </Suspense>

        {/* Render scene after assets are loaded */}
        {assetsLoaded && (
          <>
            {/* Scene environment - minimal fog, floor handles its own fade */}
            <fog attach="fog" args={[0x000000, 40, 60]} />

            {/* Camera fly-in animation */}
            {startAnimation && !introComplete && (
              <CameraFlyIn onComplete={handleIntroComplete} />
            )}

            {/* Camera controls - only mount after fly-in is complete */}
            {introComplete && (
              <CameraControls
                enabled={true}
                onCameraMoved={onCameraMoved}
                returnToHome={returnToHome}
                onReturnComplete={onReturnComplete}
              />
            )}

            {/* Scene elements */}
            <PartyLights />
            <SpaceSky textureSrc={spaceHdri} />
            <RoomFloor textureSrc={arcadeCarpetRed} />

            {/* CRT Monitor with screen content */}
            <CRTScreen.Root
              ref={crtRef}
              scene={tvScreenScene}
              fontFamily="PressStart2P"
              fontFamilies={{ PressStart2P: { normal: PressStart2PFont } }}
              isPoweredOn={tvPoweredOn}
              onPowerToggle={handlePowerToggle}
            >
              <CRTScreen.Background>
                <SceneBackground />
              </CRTScreen.Background>

              {/* Channel 1: Main content with navigation */}
              <CRTScreen.Channel channel={1}>
                <CRTScreen.Content>
                  <CRTPages routes={Routes} transitionStyle="fade" />
                </CRTScreen.Content>
                <CRTScreen.Footer>
                  <SceneFooter />
                </CRTScreen.Footer>
              </CRTScreen.Channel>

              {/* Channel 2: Test color bars */}
              <CRTScreen.Channel channel={2} noOverlay>
                <CRTScreen.Content>
                  <TestColorScreen />
                </CRTScreen.Content>
              </CRTScreen.Channel>

              {/* Channel 3: Empty/Static screen */}
              <CRTScreen.Channel channel={3} noSignal />
            </CRTScreen.Root>
          </>
        )}
      </Canvas>

      {/* Control overlay - only visible on mobile/touch devices */}
      {introComplete && (
        <ControlOverlay
          isPoweredOn={tvPoweredOn}
          onPowerToggle={handlePowerToggle}
          onVolumeUp={handleVolumeUp}
          onVolumeDown={handleVolumeDown}
          onChannelUp={handleChannelUp}
          onChannelDown={handleChannelDown}
          onReturn={onReturn}
          showReturnButton={cameraMoved && !returnToHome}
        />
      )}
    </>
  );
}
